import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NOOR } from "@/constants/testIds/noor";

const LINKS = [
  { id: "heritage", label: "Heritage", testId: NOOR.navHeritage },
  { id: "craft", label: "Craft", testId: NOOR.navCraft },
  { id: "tasting", label: "Tasting", testId: NOOR.navTasting },
  { id: "gallery", label: "Object", testId: NOOR.navGallery },
];

function navigateToSection(navigate, location, id) {
  if (location.pathname !== "/") {
    navigate(`/#${id}`);
    return;
  }
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const NAV_LINK_STYLE = { color: "var(--noor-vellum)" };
const NAV_LINK_CLS = "font-mono text-[10px] tracking-[0.34em] uppercase transition-colors";

function navOnEnter(e) { e.currentTarget.style.color = "var(--noor-rosegold)"; }
function navOnLeave(e) { e.currentTarget.style.color = "var(--noor-vellum)"; }

function DesktopSectionLink({ link, navigate, location }) {
  return (
    <button
      data-testid={link.testId}
      onClick={() => navigateToSection(navigate, location, link.id)}
      className={NAV_LINK_CLS}
      style={NAV_LINK_STYLE}
      onMouseEnter={navOnEnter}
      onMouseLeave={navOnLeave}
    >
      {link.label}
    </button>
  );
}

function DesktopRouteLink({ to, label, testId }) {
  return (
    <Link
      data-testid={testId}
      to={to}
      className={NAV_LINK_CLS}
      style={NAV_LINK_STYLE}
      onMouseEnter={navOnEnter}
      onMouseLeave={navOnLeave}
    >
      {label}
    </Link>
  );
}

function DesktopLinks({ navigate, location }) {
  return (
    <nav className="hidden lg:flex items-center gap-9">
      {LINKS.map((l) => (
        <DesktopSectionLink key={l.id} link={l} navigate={navigate} location={location} />
      ))}
      <DesktopRouteLink to="/cocktails"  label="Cocktails"     testId="nav-cocktails" />
      <DesktopRouteLink to="/stockists"  label="Where to find" testId="nav-stockists" />
      <DesktopRouteLink to="/manifesto"  label="Manifesto"     testId={NOOR.navManifesto} />
    </nav>
  );
}

function MobileMenuHeader({ onClose }) {
  return (
    <div className="flex items-center justify-between px-6 pt-7 pb-6" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
      <span className="font-display text-xl tracking-[0.18em]" style={{ color: "var(--noor-bone)" }}>
        NOOR
      </span>
      <button
        data-testid="mobile-nav-close"
        onClick={onClose}
        aria-label="Close menu"
        className="p-2"
        style={{ color: "var(--noor-rosegold)" }}
      >
        <X size={20} />
      </button>
    </div>
  );
}

const MOBILE_LINK_STYLE = { color: "var(--noor-bone)" };

function MobileSectionButton({ link, onClick }) {
  return (
    <button
      data-testid={`mobile-${link.testId}`}
      onClick={onClick}
      className="font-display text-2xl text-left py-4 transition-colors"
      style={MOBILE_LINK_STYLE}
    >
      {link.label}
    </button>
  );
}

function MobileRouteLink({ to, label, testId, onClose }) {
  return (
    <Link
      data-testid={testId}
      to={to}
      onClick={onClose}
      className="font-display text-2xl py-4"
      style={MOBILE_LINK_STYLE}
    >
      {label}
    </Link>
  );
}

function MobileMenuFooter({ onClose }) {
  return (
    <div className="px-6 mt-10">
      <Link
        data-testid={`mobile-${NOOR.navInnerCircle}`}
        to="/inner-circle"
        onClick={onClose}
        className="btn-solid w-full justify-center"
      >
        Inner Circle
      </Link>
      <p
        className="mt-8 font-mono text-[10px] leading-relaxed"
        style={{ color: "var(--noor-vellum)", letterSpacing: "0.28em", textTransform: "uppercase" }}
      >
        By invitation only<br />
        hello@noor.house
      </p>
    </div>
  );
}

function MobileMenu({ open, onClose, navigate, location }) {
  const handleSection = useCallback((id) => {
    onClose();
    // Allow drawer to close visually before scrolling.
    setTimeout(() => navigateToSection(navigate, location, id), 200);
  }, [onClose, navigate, location]);

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="mobile-nav-backdrop"
        onClick={onClose}
        className={`fixed inset-0 z-[58] lg:hidden transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{ background: "rgba(11,11,14,0.78)", backdropFilter: "blur(6px)" }}
      />
      {/* Drawer */}
      <aside
        data-testid="mobile-nav-drawer"
        className={`fixed top-0 right-0 bottom-0 z-[59] w-[82%] max-w-[340px] lg:hidden transition-transform duration-400 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        style={{
          background: "var(--noor-obsidian)",
          borderLeft: "1px solid var(--border-subtle)",
        }}
      >
        <MobileMenuHeader onClose={onClose} />
        <nav className="flex flex-col px-6 pt-8 gap-1">
          {LINKS.map((l) => (
            <MobileSectionButton key={l.id} link={l} onClick={() => handleSection(l.id)} />
          ))}
          <MobileRouteLink to="/cocktails"  label="Cocktails"     testId="mobile-nav-cocktails"               onClose={onClose} />
          <MobileRouteLink to="/stockists"  label="Where to find" testId="mobile-nav-stockists"               onClose={onClose} />
          <MobileRouteLink to="/manifesto"  label="Manifesto"     testId={`mobile-${NOOR.navManifesto}`}      onClose={onClose} />
        </nav>
        <MobileMenuFooter onClose={onClose} />
      </aside>
    </>
  );
}

// Scroll listener with built-in passive flag and cleanup.
function useScrolledPast(threshold) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

// Locks body scroll while `locked` is true. Used by the mobile drawer.
function useBodyScrollLock(locked) {
  useEffect(() => {
    document.body.style.overflow = locked ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [locked]);
}

function NavBrandMark() {
  return (
    <Link data-testid={NOOR.navLogo} to="/" className="flex items-center gap-3">
      <span
        className="font-display text-[1.55rem] tracking-[0.18em]"
        style={{ color: "var(--noor-bone)", fontWeight: 500 }}
      >
        NOOR
      </span>
      <span className="hidden md:inline overline-vellum">— Tequila Blanco</span>
    </Link>
  );
}

const INNER_CIRCLE_BTN_STYLE = { padding: "0.7rem 1.3rem", fontSize: "0.66rem" };

function NavInnerCircleCTA() {
  return (
    <Link
      data-testid={NOOR.navInnerCircle}
      to="/inner-circle"
      className="btn-ghost hidden lg:inline-flex"
      style={INNER_CIRCLE_BTN_STYLE}
    >
      Inner Circle
    </Link>
  );
}

function NavMobileToggle({ onOpen }) {
  return (
    <button
      data-testid="mobile-nav-toggle"
      onClick={onOpen}
      aria-label="Open menu"
      className="lg:hidden p-2 -mr-2"
      style={{ color: "var(--noor-rosegold)" }}
    >
      <Menu size={22} />
    </button>
  );
}

export default function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const scrolled = useScrolledPast(24);
  useBodyScrollLock(menuOpen);

  const openMenu = useCallback(() => setMenuOpen(true), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  return (
    <>
      <header
        className={`fixed left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "nav-blur py-3" : "py-5"}`}
        style={{ top: "28px" }}
      >
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 flex items-center justify-between">
          <NavBrandMark />
          <DesktopLinks navigate={navigate} location={location} />
          <NavInnerCircleCTA />
          <NavMobileToggle onOpen={openMenu} />
        </div>
      </header>
      <MobileMenu open={menuOpen} onClose={closeMenu} navigate={navigate} location={location} />
    </>
  );
}
